<script setup lang="ts">
import { i18n } from '@/i18n';
import { computed } from 'vue';

const t = i18n.t;
const props = withDefaults(
  defineProps<{
    orderGroupName: string;
    numberOfOrder: number;
    upperLimitQuantity: number;
    afterUpperLimitQuantity?: number;
    totalAmount: number;
    afterTotalAmount?: number;
    hasBackground?: boolean;
  }>(),
  {
    afterUpperLimitQuantity: undefined,
    afterTotalAmount: undefined,
    hasBackground: true,
  }
);

const upperLimitQuantityView = computed(() =>
  props.upperLimitQuantity
    ? props.afterUpperLimitQuantity
      ? `${props.upperLimitQuantity.addComma()}→${props.afterUpperLimitQuantity.addComma()}`
      : props.upperLimitQuantity.addComma()
    : 0
);

const totalAmountView = computed(() =>
  props.totalAmount
    ? props.afterTotalAmount
      ? `${props.totalAmount.addComma()}→${props.afterTotalAmount.addComma()}`
      : props.totalAmount.addComma()
    : 0
);

const loadFactor = computed(() => {
  if (props.totalAmount === undefined || props.upperLimitQuantity === undefined) {
    return 0;
  }
  return ((props.afterTotalAmount ?? props.totalAmount) * 100) / (props.afterUpperLimitQuantity ?? props.upperLimitQuantity);
});

const isLoadFactorOverLimit = computed(() => (loadFactor.value ?? -1) > 100);
</script>
<template>
  <div class="order-group-info__container" :class="{ 'order-group-info__container--background': props.hasBackground }">
    <div class="name__container">
      <div class="name__label">
        {{ t('発注グループ名') }}
      </div>
      <div class="name__value">
        {{ props.orderGroupName }}
      </div>
    </div>
    <div class="detail__container">
      <div class="detail__item">
        <div class="detail__label">
          {{ t('発注件数') }}
        </div>
        <div class="detail__value">
          {{ props.numberOfOrder }}
        </div>
      </div>
      <div class="detail__item">
        <div class="detail__label">
          {{ t('上限数量') }}
        </div>
        <div class="detail__value">
          {{ upperLimitQuantityView }}
        </div>
      </div>
      <div class="detail__item">
        <div class="detail__label">
          {{ t('発注合計数量') }}
        </div>
        <div class="detail__value">
          {{ totalAmountView }}
        </div>
      </div>
      <div class="detail__item">
        <div class="detail__label">
          {{ t('積載率') }}
        </div>
        <div class="detail__value" :class="{ 'detail__value--error': isLoadFactorOverLimit }">
          {{ `${loadFactor.roundToDecimal(1)}%` }}
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.order-group-info__container {
  padding: 16px;
  display: grid;
  grid-template-columns: 192px auto;
  * {
    line-height: 1;
  }
  &--background {
    border-radius: 8px;
    background-color: #f5f5f5;
  }
}

.name {
  &__label {
    padding: 8px 0;
  }
  &__value {
    padding: 8px;
  }
}

.detail {
  &__container {
    display: grid;
    grid-template-columns: 1fr 1fr 2.5fr 1fr;
    align-items: center;
  }
  &__item {
    display: grid;
    justify-items: center;
    gap: 8px;
    * {
      width: fit-content;
      height: fit-content;
    }
  }
  &__label {
    color: $textSecondary;
  }
  &__value {
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    &--error {
      color: $semanticError;
    }
  }
}
</style>
